import { isProblemDetails, type components } from '@hpd/contract';
import { Temporal } from '@js-temporal/polyfill';
import { queryOptions } from '@tanstack/react-query';

import { api } from '../api.ts';
import type { FulfilmentInput } from './jobs.ts';
import { ApiProblem } from './me.ts';

// CB-REQ QUO-01 — the priced quote (v1.943). Money on a quote arrives only for someone above the
// price line; below it, rates and totals are absent from the response and the screens show the
// lines without them.

export type Quote = components['schemas']['QuoteView'];
export type QuoteSummary = components['schemas']['QuoteSummaryView'];
export type QuoteLine = components['schemas']['QuoteLineView'];
export type TaxCode = components['schemas']['TaxCodeView'];

export type LocalWindow = { start: string; end: string };

/** CB-REQ JOB-02 at v1.958: a produced event's bump-in, event and bump-out, as wall-clock times. */
export type EventWindowsInput = { bumpIn: LocalWindow; event: LocalWindow; bumpOut: LocalWindow };

/** Left out keeps a detail; null (or blank) clears it. */
export type SiteDetailsInput = {
  contactName?: string | null;
  contactPhone?: string | null;
  loadNotes?: string | null;
};

export const TYPE_LABELS: Record<Quote['type'], string> = {
  dry_hire: 'Dry hire',
  produced_event: 'Produced event',
};

export type LineDiscountInput =
  { kind: 'percent'; percent: string } | { kind: 'amount'; amountMinor: string };

export type LineInput = {
  productId: string;
  quantity: number;
  chargeableDays?: string[] | null;
  measure?: string | null;
  discount?: LineDiscountInput | null;
  discountReason?: string;
  taxCodeId?: string;
  /** JOB-14 at v1.958: a room of the quote's venue, on a produced event. */
  roomId?: string | null;
};

export type LineChanges = Partial<Omit<LineInput, 'productId'>>;

export const STATUS_LABELS: Record<Quote['status'], string> = {
  draft: 'Draft',
  sent: 'Sent',
  declined: 'Declined',
  superseded: 'Superseded',
  accepted: 'Accepted',
};

function problem(error: unknown, fallback: string): ApiProblem {
  if (!isProblemDetails(error)) return new ApiProblem('unknown', fallback);
  const { type, title, detail, ...extensions } = error as typeof error & Record<string, unknown>;
  return new ApiProblem(type, detail ?? title, extensions);
}

export const quotesQuery = queryOptions({
  queryKey: ['quotes'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/quotes');
    if (error) throw problem(error, 'Could not load the quotes.');
    return data;
  },
});

export const quoteQuery = (id: string) =>
  queryOptions({
    queryKey: ['quotes', id],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/quotes/{id}', { params: { path: { id } } });
      if (error) throw problem(error, 'Could not load that quote.');
      return data;
    },
  });

export const taxCodesQuery = queryOptions({
  queryKey: ['tax-codes'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/tax-codes');
    if (error) throw problem(error, 'Could not load the tax codes.');
    return data;
  },
});

export async function createQuote(input: {
  clientId: string;
  type: Quote['type'];
  soldWindow: LocalWindow;
  scheduledWindow?: LocalWindow;
  eventWindows?: EventWindowsInput;
  site?: SiteDetailsInput;
  venueId?: string | null;
  notes?: string;
}) {
  const { data, error } = await api.POST('/v1/quotes', { body: input });
  if (error) throw problem(error, 'Could not create that quote.');
  return data;
}

export async function updateQuote(
  id: string,
  changes: {
    soldWindow?: LocalWindow;
    scheduledWindow?: LocalWindow;
    notes?: string | null;
    /** VEN-11 at v1.989: null clears it. */
    endClientName?: string | null;
    eventWindows?: EventWindowsInput;
    site?: SiteDetailsInput;
  },
) {
  const { data, error } = await api.PATCH('/v1/quotes/{id}', {
    params: { path: { id } },
    body: changes,
  });
  if (error) throw problem(error, 'Could not save those changes.');
  return data;
}

export async function addLine(quoteId: string, input: LineInput) {
  const { data, error } = await api.POST('/v1/quotes/{id}/lines', {
    params: { path: { id: quoteId } },
    body: input,
  });
  if (error) throw problem(error, 'Could not add that line.');
  return data;
}

export async function updateLine(quoteId: string, lineId: string, changes: LineChanges) {
  const { data, error } = await api.PATCH('/v1/quotes/{id}/lines/{lineId}', {
    params: { path: { id: quoteId, lineId } },
    body: changes,
  });
  if (error) throw problem(error, 'Could not change that line.');
  return data;
}

export async function removeLine(quoteId: string, lineId: string) {
  const { data, error } = await api.DELETE('/v1/quotes/{id}/lines/{lineId}', {
    params: { path: { id: quoteId, lineId } },
  });
  if (error) throw problem(error, 'Could not remove that line.');
  return data;
}

/** QUO-03 at v1.944: with lines short, the API asks first; acknowledging is the answer. */
export async function sendQuote(id: string, acknowledgeShortages = false) {
  const { data, error } = await api.POST('/v1/quotes/{id}/send', {
    params: { path: { id } },
    body: { acknowledgeShortages },
  });
  if (error) throw problem(error, 'Could not mark that quote as sent.');
  return data;
}

/** AVL-03 at v1.944: "Hold firmly", or back to a pencil. */
export async function holdLine(quoteId: string, lineId: string, firm: boolean) {
  const { data, error } = await api.POST('/v1/quotes/{id}/lines/{lineId}/hold', {
    params: { path: { id: quoteId, lineId } },
    body: { firm },
  });
  if (error) throw problem(error, 'Could not change that hold.');
  return data;
}

/**
 * QUO-01 at v1.946: acceptance creates the job. With lines short the API always asks first;
 * acknowledging is the answer, and those lines then stay pencilled on the job (AVL-03).
 */
export async function acceptQuote(
  id: string,
  input: { acknowledgeShortages: boolean; clientPo?: string },
) {
  const { data, error } = await api.POST('/v1/quotes/{id}/accept', {
    params: { path: { id } },
    body: input,
  });
  if (error) throw problem(error, 'Could not accept that quote.');
  return data;
}

/** QUO-01 at v1.946: collection or delivery, on a draft or sent quote. */
export async function setQuoteFulfilment(id: string, input: FulfilmentInput) {
  const { data, error } = await api.PUT('/v1/quotes/{id}/fulfilment', {
    params: { path: { id } },
    body: input,
  });
  if (error) throw problem(error, 'Could not change how the gear gets there.');
  return data;
}

export async function declineQuote(id: string) {
  const { data, error } = await api.POST('/v1/quotes/{id}/decline', { params: { path: { id } } });
  if (error) throw problem(error, 'Could not decline that quote.');
  return data;
}

export async function reviseQuote(id: string, reason: string) {
  const { data, error } = await api.POST('/v1/quotes/{id}/revise', {
    params: { path: { id } },
    body: { reason },
  });
  if (error) throw problem(error, 'Could not revise that quote.');
  return data;
}

/**
 * The sold window from the days the client is charged for, as a dry hire is described: "out Friday
 * 10:00, back Monday 10:00, charged Saturday". Charged days run from local midnight, clamped inside
 * the scheduled window, so the sold window is never wider than the gear is committed (OPS-04).
 * Local wall-clock strings in one zone compare correctly as text.
 */
export function soldWindowFor(
  scheduled: LocalWindow,
  firstChargedDay: string,
  lastChargedDay: string,
): LocalWindow {
  const start = `${firstChargedDay}T00:00`;
  const end = `${nextDay(lastChargedDay)}T00:00`;
  return {
    start: start < scheduled.start ? scheduled.start : start,
    end: end > scheduled.end ? scheduled.end : end,
  };
}

function nextDay(date: string): string {
  return Temporal.PlainDate.from(date).add({ days: 1 }).toString();
}

/**
 * "Sat 3 Oct" — a local date as the office says it, with its year when it is not this year
 * ("Wed 10 Feb 2027"), so a list spanning New Year reads right. A plain date: no zone arithmetic.
 */
export function dayLabel(date: string): string {
  const day = Temporal.PlainDate.from(date);
  const label = day.toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
  return day.year === Temporal.Now.plainDateISO().year ? label : `${label} ${day.year}`;
}

/** "Wed 7 Oct 2026" — a date always with its year. */
export function dayWithYear(date: string): string {
  const day = Temporal.PlainDate.from(date);
  return `${day.toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })} ${day.year}`;
}

/**
 * The tenant's calendar day an instant falls on (INV-02). Cutting the date off the ISO string reads
 * it in UTC, which put a Melbourne 07:00 start on the day before.
 */
export function tenantDay(instant: string, zone: string): string {
  return Temporal.Instant.from(instant).toZonedDateTimeISO(zone).toPlainDate().toString();
}

/** "Fri 2 Oct, 10:00" from a local wall-clock string. */
export function localLabel(local: string): string {
  const [date, time] = local.split('T');
  return `${dayLabel(date!)}, ${time}`;
}

/** A produced event's first and last show days. An event ending at midnight ends the day before. */
export function eventDays(events: EventWindowsInput): { first: string; last: string } {
  const end = events.event.end;
  return {
    first: events.event.start.slice(0, 10),
    last: end.endsWith('T00:00')
      ? Temporal.PlainDate.from(end.slice(0, 10)).subtract({ days: 1 }).toString()
      : end.slice(0, 10),
  };
}

/**
 * OPS-04 at v1.958: a produced event's sold window — from the first charged day to the last, the
 * event's days unless said otherwise, inside the span from bump-in to bump-out.
 */
export function eventSoldWindow(
  events: EventWindowsInput,
  first: string = eventDays(events).first,
  last: string = eventDays(events).last,
): LocalWindow {
  return soldWindowFor({ start: events.bumpIn.start, end: events.bumpOut.end }, first, last);
}

/** INV-A14 at v1.983: this booking leaves out one of the venue's inclusions, or takes it back. */
export async function setInclusionDeclined(
  quoteId: string,
  inclusionId: string,
  declined: boolean,
) {
  const path = { params: { path: { id: quoteId, inclusionId } } };
  const { data, error } = declined
    ? await api.PUT('/v1/quotes/{id}/declined-inclusions/{inclusionId}', path)
    : await api.DELETE('/v1/quotes/{id}/declined-inclusions/{inclusionId}', path);
  if (error) throw problem(error, 'Could not change what this booking includes.');
  return data;
}
